import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Table,
  TableActionTd,
  TableActionTh,
  TableBody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@widgets/table';
import Paginator, { DEFAULT_PER_PAGE } from '@widgets/paginator';
import {
  useFilters,
  useLimit,
  useListParamsAndPersistToUrl,
  usePage,
  useSort,
} from '@app/hooks/useList';
import { useUrlParams } from '@app/hooks/useUrlParams';
import { setBreadcrumbs } from '@app/redux/features/breadcrumbsSlice';
import { BreadCrumbIcon, BreadCrumbType } from '@entities/util/IBreadCrumb';
import Section from '@widgets/components/section';
import { GrayButton } from '@shared/uikit/button';
import IconDownload from '@shared/icons/IconDownload';
import ValueWithSign from '@widgets/table/components/value-with-sign';
import EmptyList from '@widgets/empty-list';
import { ListLoader, Loader } from '@shared/uikit/loader';
import {
  IBoeingIncomeAccountList,
  useBoeingNewAccountList,
} from '@app/queries/boeing/useBoeingNewAccountList';
import IncomeHashRateFilter, {
  IIncomeHashRateFilter,
} from '@pages/Main/components/IncomeHashRate/filter';
import { roundNumberToTwoPrecision } from '@app/utils/math';
import ValueWithPercentageMonitor from '@widgets/table/components/value-with-percentage-monitor';
import { useBoeingNewAccounts } from '@app/queries/boeing/useBoeingNewAccounts';

const IncomeHashRateShow = () => {
  const dispatch = useDispatch();

  const boeingNewAccountListFetcher = useBoeingNewAccountList();
  const boeingNewAccounts = useBoeingNewAccounts();

  const { urlParams } = useUrlParams();
  const page = usePage(urlParams, 1);
  const perPage = useLimit(urlParams, DEFAULT_PER_PAGE);
  const filtering = useFilters(urlParams, []);
  const sorting = useSort(urlParams);
  useListParamsAndPersistToUrl(page, perPage, filtering, sorting);

  const [newAccounts, setNewAccounts] = useState<{
    accounts: number;
    clients: number;
    hashrate: number;
  } | null>(null);

  const [bulkItems, setBulkItems] = useState<number[]>([]);
  const [list, setList] = useState<IBoeingIncomeAccountList | null>(null);

  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          title: 'Sales DB',
          type: BreadCrumbType.LINK,
          link: '/main',
          icon: BreadCrumbIcon.VIA_BTC,
        },
        {
          title: 'Основные показатели',
          type: BreadCrumbType.LINK,
          link: '/main',
        },
        { title: 'Приток нового хешрейта', type: BreadCrumbType.TEXT },
      ])
    );
  }, []);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      const newAccounts = await boeingNewAccounts.fetch(filtering.value);
      setNewAccounts({
        accounts: newAccounts.new_accounts,
        clients: newAccounts.new_clients,
        hashrate: newAccounts.new_hashrate,
      });
    })();
  }, [filtering.value]);

  useEffect(() => {
    (async () => {
      if (null === filtering.value) return;

      setList(null);
      const data = await boeingNewAccountListFetcher.fetch({
        ...filtering.value,
        page: page.value ?? 1,
        sort: sorting.value,
        perPage: perPage.value ?? DEFAULT_PER_PAGE,
      });
      setList(data);
    })();
  }, [page.value, perPage.value, filtering.value, sorting.value?.column, sorting.value?.type]);

  const handlePageClick = useCallback(
    (selectedItem: { selected: number }) => page.setValue(selectedItem.selected + 1),
    []
  );

  const onChangePerPage = useCallback(
    (count: number) => {
      perPage.setValue(count);
      setBulkItems([]);
    },
    [perPage.value]
  );

  return (
    <>
      <div className="flex justify-between items-center w-full">
        <IncomeHashRateFilter
          filters={filtering.value}
          onChangeFilters={(values: IIncomeHashRateFilter) => filtering.setValue(values)}
          onReset={() => {
            // const parsedReportLastSyncDate = new Date(reportLastSyncDate?.split('.')?.reverse());

            page.setValue(1);
            perPage.setValue(DEFAULT_PER_PAGE);
            sorting.setValue(null);
            filtering.setValue([]);
          }}
        />
        <div
          className="flex gap-[10px] self-stretch border-t border-x border-[#95959540]
                    rounded-t-[16px] z-10 relative bg-[#141316]"
        >
          <div className="absolute w-full h-[20px] bg-[#141316] -bottom-[2px] left-0"></div>

          {newAccounts ? (
            <div className="flex w-[208px] px-[30px] justify-between items-center">
              <div className="text-[12px] text-[#777777]">Всего</div>
              <div className="flex justify-end items-end gap-[5px]">
                <span className="text-[18px]">{newAccounts?.hashrate}</span>&nbsp;
                <span className="text-[12px] text-[#777777] mb-[3px]">Ph</span>
              </div>
            </div>
          ) : (
            <div className="w-[208px] flex items-center justify-center">
              <Loader size={20} loading={true} />
            </div>
          )}
        </div>
      </div>

      <div
        className="px-[24px] mb-[20px] border border-opacity-30 solid rounded-[16px] border-[#959595]
                h-[100%] w-[100%] rounded-tr-[0px] relative max-h-[78vh] overflow-y-auto"
      >
        {boeingNewAccountListFetcher.isSuccess && list !== null ? (
          <>
            {list.items.length ? (
              <>
                <Table>
                  <TableThead className="sticky z-20 top-0 bg-[#141316]">
                    <TableTr>
                      <TableActionTh
                        className="w-[3%] pt-[30px]"
                        onClick={() => {
                          if (list.items.length === bulkItems.length) {
                            setBulkItems([]);
                          } else {
                            setBulkItems(list.items.map(item => item.client_id));
                          }
                        }}
                      />
                      <TableTh
                        title="Клиент"
                        className="w-[15%] pt-[30px]"
                        sortName="client_name"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Аккаунт"
                        className="w-[13%] pt-[30px]"
                        sortName="account"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Начало майнинга"
                        className="w-[10%] pt-[30px]"
                        sortName="mining_start_at"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Менеджер"
                        className="w-[13%] pt-[30px]"
                        sortName="manager"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Источник"
                        className="w-[13%] pt-[30px]"
                        sortName="source"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Тег"
                        className="w-[13%] pt-[30px]"
                        sortName="tag"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Текущий хешрейт"
                        className="w-[8%] pt-[30px]"
                        sortName="hashrate"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                      <TableTh
                        title="Разница хешрейта"
                        className="w-[15%] pt-[30px]"
                        sortName="hashrate_diff"
                        sort={sorting.value}
                        onSort={sorting.setValue}
                      />
                    </TableTr>
                  </TableThead>
                  <TableBody>
                    {list &&
                      list.items.map((item, key) => (
                        <TableTr key={key}>
                          <TableActionTd
                            items={bulkItems}
                            value={item.client_id}
                            onChange={setBulkItems}
                          />
                          <TableTd
                            title={
                              <ValueWithSign
                                value={item.client_name ?? 'Нет клиента'}
                                color="bg-[#4598E6]"
                              />
                            }
                          />
                          <TableTd
                            title={
                              <div>
                                <ValueWithSign value={item.account} color="bg-[#99840F]" />
                              </div>
                            }
                          />
                          <TableTd title={`${item.mining_start_at ?? '-'}`} />
                          <TableTd title={item.manager_name} />
                          <TableTd title={item.source} />
                          <TableTd title={item.tag} />
                          <TableTd title={item.hashrate + ' PH'} />
                          <TableTd
                            title={
                              <ValueWithPercentageMonitor
                                value={roundNumberToTwoPrecision(item.hashrate_diff) + ' Ph'}
                                percentage={roundNumberToTwoPrecision(
                                  item.hashrate_percentage_diff
                                )}
                                mode={item.hashrate_percentage_diff > 0 ? 'up' : 'down'}
                              />
                            }
                          />
                        </TableTr>
                      ))}
                  </TableBody>
                </Table>

                <div className="py-[30px] flex justify-end items-center w-full sticky z-20 bottom-0 bg-[#141316]">
                  <Paginator
                    totalPages={list.total_pages}
                    page={page.value ? page.value - 1 : 0}
                    perPage={perPage.value ?? DEFAULT_PER_PAGE}
                    onChangePerPage={onChangePerPage}
                    onChangePage={handlePageClick}
                    totalItems={`${list?.total_items} аккаунтов`}
                  />
                </div>
              </>
            ) : (
              <EmptyList />
            )}
          </>
        ) : (
          <ListLoader loading={true} />
        )}
      </div>
    </>
  );
};

export default IncomeHashRateShow;
